import { useState, useEffect, useRef, DOMElement, JSXElementConstructor, ReactElement, ReactNode, ReactPortal } from "react";
import { useLocation, useOutletContext, useParams } from "react-router-dom";

import CodeSnippet from "../components/CodeSnippet";
import axios from "../scripts/axiosConfig";
import { useAuth } from "../contexts/AuthContext";
import MessagePopup from "../components/MessagePopup";
import Tag from "../components/Tag";

import AceEditor from "react-ace";
import modeJava from "ace-builds/src-noconflict/mode-java";
import modePython from "ace-builds/src-noconflict/mode-python";
import modeCCPP from "ace-builds/src-noconflict/mode-c_cpp";
import modeJavascript from "ace-builds/src-noconflict/mode-javascript";

import { config } from "ace-builds";
import "ace-builds/src-noconflict/snippets/java";
import "ace-builds/src-noconflict/snippets/python";
import "ace-builds/src-noconflict/snippets/c_cpp";
import "ace-builds/src-noconflict/snippets/javascript";
import "ace-builds/src-noconflict/ext-language_tools";

import monokaiTheme from "ace-builds/src-noconflict/theme-monokai";
import "../utils/ace-custom-theme";
// import "ace-builds/src-noconflict/ext-language_tools";

import { HiOutlineSave } from "react-icons/hi";
import { IoIosLogOut } from "react-icons/io";
import { IoChevronBackOutline } from "react-icons/io5";
import { AiOutlineLoading } from "react-icons/ai";

import "../styles/Code.scss";

import { toast } from "react-toastify";
import CLink from "../components/CLink";
import { AxiosError } from "axios";
import { AnimatePresence, motion } from "framer-motion";
import CodeRunStatus from "../components/CodeRunStatus";
import { CodeRunStatusKey } from "../components/CodeRunStatus/types";


type TestcaseType = {
    testcaseIndex: number,
    input: string,
    run_status: "SUCCESS" | "ERROR",
    run_error: {
        cmd: string,
        code: string,
        signal: string,
        killed: boolean,
    } | null | string,
    output: string,
    correctOutput: null | boolean,
    score: number,
    testCase0Output: string,
    passedTestcases: number,
}

const CodeLanguages = ["CPP17", "JAVA8", "PYTHON3", "JAVASCRIPT_NODE"] as const;

type CodeLanguageType = (typeof CodeLanguages)[number];

type CodeStorageType = {
    [problemID: string]: {
        [lang in CodeLanguageType]: {
            "original": string,
            "user": string
        }
    };
};

type InitialCodeType = {
    language: typeof CodeLanguages,
    code: string,
    _id: string
}

type ProblemType = {
    id: string,
    _id: string,
    title: string,
    testcaseLength: number,
    status: "attempted" | "not_attempted" | "solved",
    score: number,
    sampleInput: string,
    sampleOutput: string,
    problemScore: number,
    problemStatus: string,
    inputFormat: string,
    outputFormat: string,
    description: string,
    explanation: string,
    constraints: string,
    code?: string, // Optional property for storing code
    initialCode: InitialCodeType[]
}

const Code = () => {
    const { 
        type, 
        codeStorage,
        setCodeStorage,
        contestDuration,
        endTime,
        openForever,
        timedContest,
        proctored,
        setPageToShow, 
        problemsList, 
        finishRound 
    } = useOutletContext<any>();
    
    const [testcaseResults, setTestcaseResults] = useState<TestcaseType[][]>([]);

    const editorToThemeLangMap = {
        "JAVA8": "java",
        "PYTHON3": "python",
        "CPP17": "c_cpp",
        "JAVASCRIPT_NODE": "javascript"
    }

    const { currentUser } = useAuth();
    const { contestSlug } = useParams();
    const location = useLocation();

    const editorRef = useRef<AceEditor>(null);
    const handleRef = useRef<HTMLDivElement>(null);
    const runStatusRef = useRef<HTMLDivElement | null>(null);

    const timerIcon = useRef<HTMLSpanElement>(null);
    const timerFullValue = useRef<HTMLSpanElement>(null);

    const [codeTimerInterval, setCodeTimerInterval] = useState<NodeJS.Timer>();
    const [loseFocusTimer, setLoseFocusTimer] = useState<NodeJS.Timer>();

    const [contestTimerValue, setContestTimerValue] = useState(99);

    const [selectedProblemIndex, setSelectedProblemIndex] = useState(0);
    const [sampleOutput, setSampleOutput] = useState<string>();

    const [runButtonDisabled, setRunButtonDisabled] = useState(false);
    const [isCodeRunning, setIsCodeRunning] = useState(false);
    const [runStatus, setRunStatus] = useState("QUEUED")

    const [flexValue, setFlexValue] = useState(0.4);

    const [chosenLanguage, setChosenLanguage] = useState<keyof typeof editorToThemeLangMap>("PYTHON3");
    const [prevChosenLanguage, setPrevChosenLanguage] = useState(chosenLanguage);
    const [initialRenderCall, setInitialRenderCall] = useState(true);
    const [firstTimeCodeStorage, setFirstTimeCodeStorage] = useState(true);

    const [savingIndicator, setSavingIndicator] = useState("");
    const [showMessagePopup, setShowMessagePopup] = useState(false);

    const [popupTitle, setPopupTitle] = useState<string>();
    const [popupContent, setPopupContent] = useState<string>();
    const [popupButtons, setPopupButtons] = useState<any>(null);

    const [blurCount, setBlurCount] = useState(0);


    const openMessagePopup = (title: string, content: string, buttons: any) => {
        setPopupTitle(title);
        setPopupContent(content);
        setPopupButtons(buttons);
        setShowMessagePopup(true);
    }

    const saveUserCodeLocally = (problemIndex: number, code: string | null, prevChosenLanguage?: string) => {
        console.log(`%c new code: ${code}`, "color: orange");

        console.log(`Changing parent problemsUserCode ${chosenLanguage} array to`, code)
        showLocalSaveIcon();
        console.log( (prevChosenLanguage) ? "saving in previous language" : "saving in current language." );
    };

    const showLocalSaveIcon = () => {
        setSavingIndicator("saving locally...");
        setTimeout(() => {
            setSavingIndicator("");
        }, 1000);
    }

    const logOutFromCurrentSession = () => {
        axios.post("/contests/finish", { contestSlug: contestSlug }, { headers: { Authorization: `Bearer ${currentUser.accessToken}` } })
        .then((res) => {
            setPageToShow("loggedout");
            clearInterval(codeTimerInterval as ReturnType<typeof setInterval>);
            clearInterval(loseFocusTimer as ReturnType<typeof setInterval>);
            document.exitFullscreen();
        })
        .catch((err) => {
            toast.error(err);
            console.log("Error in logging out from contest: ");
            console.error(err);
        })
    }

    const changeProblem = (newIndex: number) => {
        showLocalSaveIcon();
        console.log(`----------- changeProblem called to ${newIndex} -----------`)
        saveUserCodeLocally(selectedProblemIndex, editorRef.current?.editor?.getValue() ?? null);
        setSelectedProblemIndex(newIndex);
        // setCodeRunningStatus(testcaseResults[newIndex]?.[0]?.testCase0Output);
        setSampleOutput(problemsList.value[newIndex].sampleOutput);

        console.log(`Selected Problem:`);
        console.log(problemsList.value[newIndex]);

        let currentCodeTemp = editorRef.current?.editor.getValue();

        const currentProblemId = problemsList.value[selectedProblemIndex]?.id as string;
        let tempCodeStorage = codeStorage;

        console.log(`Current Problem ID: ${currentProblemId}`)
        if (currentProblemId && codeStorage[currentProblemId]) {
            console.log(currentProblemId)
            console.log(chosenLanguage)

            tempCodeStorage[currentProblemId][chosenLanguage]['user'] = currentCodeTemp ?? "";

            console.log("%c New code Storage: ", "color: orange")
            console.log(tempCodeStorage)

            setCodeStorage(tempCodeStorage);
        }
        else {
            console.log("%c Current problem ID not found in code storage.", "color: orange; background-color: black")
        }


        if (tempCodeStorage[problemsList.value[newIndex].id]) {
            console.log("New problem's original code:");
            console.log(tempCodeStorage[problemsList.value[newIndex].id][chosenLanguage]['original']);

            if (tempCodeStorage[problemsList.value[newIndex].id][chosenLanguage]['user'] !== "") {
                editorRef.current?.editor.setValue(tempCodeStorage[problemsList.value[newIndex].id][chosenLanguage]['user']);
            }
            else {
                editorRef.current?.editor.setValue(tempCodeStorage[problemsList.value[newIndex].id][chosenLanguage]['original']);
            }
        }
        else {
            console.log("%c Did not change editor code as nothing was found under problemID in codeStorage.", "color: orange; background-color: black")
        }
    }

    // ON LANGUAGE CHANGE
    useEffect(() => {
        console.log("%c LANGUAGE CHANGED", "color: pink");
        if (initialRenderCall) {
            console.log("%c Initial render call, ignoring.", "color: pink");
            setInitialRenderCall(false);
            return;
        }
        console.log(prevChosenLanguage, " -> ", chosenLanguage);

        let currentCodeTemp = editorRef.current?.editor.getValue();
        const currentProblemId = problemsList.value[selectedProblemIndex]?.id as string;
        let tempCodeStorage = codeStorage;

        // saving process.
        if (currentProblemId && codeStorage[currentProblemId]) {
            tempCodeStorage[currentProblemId][prevChosenLanguage]['user'] = currentCodeTemp ?? "";
            setCodeStorage(tempCodeStorage);
        }
        else {
            console.log("%c Current problem ID not found in code storage.", "color: orange; background-color: black")
        }

        // retrieving process.
        if (tempCodeStorage[problemsList.value[selectedProblemIndex].id]) {
            if (tempCodeStorage[problemsList.value[selectedProblemIndex].id][chosenLanguage]['user'] !== "") {
                console.log("Changing code to user code of new language.")
                editorRef.current?.editor.setValue(tempCodeStorage[problemsList.value[selectedProblemIndex].id][chosenLanguage]['user']);
            }
            else {
                console.log("Changing code to original code of new language.")
                editorRef.current?.editor.setValue(tempCodeStorage[problemsList.value[selectedProblemIndex].id][chosenLanguage]['original']);
            }
        }
        else {
            console.log("%c Did not change editor code as nothing was found under problemID in codeStorage.", "color: orange; background-color: black")
        }

    }, [chosenLanguage])

    const runCode = async () => {
        let editorCode = editorRef?.current?.editor.getValue();
        if (!editorCode) {
            toast.error("Code cannot be empty.")
            return;
        }
        setRunButtonDisabled(true);

        runStatusRef?.current?.scrollIntoView();
        saveUserCodeLocally(selectedProblemIndex, editorCode);
        console.log(editorCode);

        // setCodeRunningStatus("Running code...");
        setIsCodeRunning(true);

        console.log("Running code...");
        console.log("Code Type: ", type);
        console.log("Current User:", JSON.stringify(currentUser));

        let userToken: string | null = null;

        if (currentUser && currentUser !== "none") {
            userToken = `Bearer ${await currentUser.getIdToken()}`;
        }
        
        axios.post("/code/submit", {
            code: editorCode,
            language: chosenLanguage,
            contestSlug: contestSlug,
            problemID: problemsList.value[selectedProblemIndex].id,
            type,
         }, { headers: { Authorization: userToken } })
         .then((res) => {
            console.log(res)

            if (res.data.submissionID) {
                setRunStatus(res.data.status);
                
                let pollAttempts = 0;

                const pollSubmissionStatus = async () => {
                    if (pollAttempts >= 5) {
                        console.log("Polling attempts exceeded 5. Exiting polling.")
                        setIsCodeRunning(true);
                        setRunStatus("ERROR_NO_SERVERS_ONLINE");
                        setRunButtonDisabled(false);
                        return;
                    }

                    pollAttempts++;

                    try {
                        const pollResponse = await axios.get(`/code/status/${res.data.submissionID}`, { headers: {  Authorization: userToken } });
                        console.log(pollResponse);

                        setRunStatus(pollResponse.data.status);

                        if (pollResponse.data.status === "DONE") {
                            setRunButtonDisabled(false);
                            setIsCodeRunning(false);

                            let currentProblemResults = pollResponse.data.results; // this is an array containing testcase result objs.
                            let tempArray: any = [...testcaseResults];

                            console.log("Before setting current problem test case results: ");
                            console.log(tempArray);

                            tempArray[selectedProblemIndex] = currentProblemResults;
                            setTestcaseResults(tempArray);

                            console.log("After setting current problem test case results:");
                            console.log(tempArray);

                            return; // Exit poll after completion
                        }

                        // Wait 5 seconds before the next poll
                        setTimeout(pollSubmissionStatus, 5000);
                    } catch (err: unknown) {
                        console.log(err);
                        setRunStatus("ERROR_UNKNOWN");
                        if (err instanceof Error) {
                            toast.error(err.message);
                        } else {
                            toast.error("An unknown error occurred.");
                        }
                        setRunButtonDisabled(false);
                        setIsCodeRunning(false);
                    }
                };

                pollSubmissionStatus();
            }
         })
         .catch((err: AxiosError) => {
            console.log("%c ERROR FROM CODE RUN API", "color: orange")
            console.log(err);
            toast.error((err.response?.data as any)?.message || "An error occurred while running the code.");
            setRunButtonDisabled(false);
            setIsCodeRunning(false);

            if (err.response && typeof err.response.data == typeof AxiosError && err.response.data !== null && (err.response.data as any)["SUCCESSFUL"] === false) {
                setRunStatus(`ERROR_${(err.response.data as any)['MESSAGE']}`);
            }
         });
    };


    const formatTime = (seconds: number, returnFullTime?: boolean) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = seconds % 60;

        if (returnFullTime) {
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
        }
        else {
            if (hours > 0) {
                return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
            } else {
                return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
            }
        }
    };

    const getCodeForLang = (problem: ProblemType, language: CodeLanguageType) => {
        return ( problem.initialCode?.find((codeObj: any) => codeObj.language === language)?.code || "" );
    }

      
    useEffect(() => {
        setTimeout(() => setFlexValue(0.4), 10);

        console.log("-------------=-===========================-------------------------")
        console.log(problemsList.value);
        console.log("-------------=-===========================-------------------------")

        config.setModuleUrl("ace/mode/java", modeJava);
        config.setModuleUrl("ace/mode/c_cpp", modeCCPP);
        config.setModuleUrl("ace/mode/python", modePython);
        config.setModuleUrl("ace/mode/javascript", modeJavascript);

        config.setModuleUrl("ace/theme/monokai", monokaiTheme);

        if (type === "problem") {

        }
        else if (type === "challenge") {

        }
        else if (type === "contest") {
            // Proctored mode is only available for contests.
            if (proctored) {
                // set to fullscreen
                document.addEventListener("fullscreenchange", handleFullScreenChange);
                document.documentElement.requestFullscreen();
                window.onblur = handleBlur;

                // SECURITY
                window.oncontextmenu = () => {
                    toast.error("Right click is not allowed.");
                    return false;
                };
                
                
                // F12
                window.addEventListener('keydown', function(event) {
                    if (event.keyCode === 123) {
                        event.preventDefault();
                    }
                });

                // prevnt ctrl shift i
                window.addEventListener('keydown', function(event) {
                    if (event.ctrlKey && event.shiftKey && event.keyCode === 73) {
                        event.preventDefault();
                    }
                });

                // prevnt copy
                window.addEventListener('keydown', function(event) {
                    if (event.ctrlKey && event.keyCode === 67) {
                        event.preventDefault();
                    }
                });

                // prevnt paste
                window.addEventListener('keydown', function(event) {
                    if (event.ctrlKey && event.keyCode === 86) {
                        event.preventDefault();
                    }
                });
            }
            else {
                console.log("Contest is not proctored. No need to go fullscreen.")
            }
            // SECURITY END.
            
            // Timing controls are only available for contests.
            let warnedLessThan10 = false;
            let warnedLessThan5 = false;
            let warnedLessThan1 = false;
    
            if (!timedContest && openForever) {
                console.log("Contest is untimed and is open forever. No need to set timer.");
            } else {
                let value = 0;
                let showTimer = true;
    
                if (timedContest && !openForever) {
                    console.log("Contest is timed and also has an end time. Calculating which is shorter and setting timer.");
    
                    let diff = new Date(endTime).getTime() - new Date().getTime();
                    let secondsUntilContestEnds = Math.round(diff / 1000);
                    console.log("Contest ends at:", new Date(endTime));
                    console.log("Seconds left until contest ends:", secondsUntilContestEnds);
                    console.log("Contest Duration:", contestDuration)
                    
                    value = Math.min(secondsUntilContestEnds, contestDuration);
                    console.log("Timer set to:", value);
                } else if (!timedContest && !openForever) {
                    console.log("Contest is untimed and has an end time. Setting timer.");
    
                    let diff = new Date(endTime).getTime() - new Date().getTime();
                    value = Math.round(diff / 1000);
    
                    console.log("Contest ends at:", new Date(endTime));
                    console.log("Timer set to:", value);
                } else if (openForever && timedContest) {
                    console.log("Contest is open forever, but has a duration. Setting timer.");
                    value = contestDuration;
    
                    console.log("Timer set to:", value);
                }
    
                if (showTimer && value > 0) {
                    if (codeTimerInterval) {
                        clearInterval(codeTimerInterval as any);
                    }
    
                    const timer = setInterval(() => {
                        value--;
    
                        if (value <= 0) {
                            toast.info("Contest has ended.");
                            clearInterval(timer);
                            openMessagePopup(
                                "Saving your progress...",
                                "The contest has ended. Your progress is being automatically saved. Please do not close the tab until this process completes.",
                                []
                            );
                            setPageToShow("ended");
                        } else if (value <= 60 && !warnedLessThan1) {
                            warnedLessThan1 = true;
                            toast.warn("Contest ends in less than one minute. Complete fast!");
                        } else if (value <= 300 && !warnedLessThan5) {
                            warnedLessThan5 = true;
                            toast.warn("Contest ends in less than 5 minutes.");
                        } else if (value <= 600 && !warnedLessThan10) {
                            warnedLessThan10 = true;
                            toast.info("Contest ends in less than 10 minutes.");
                        }
    
                        setContestTimerValue(value);
    
                        if (timerIcon.current) {
                            timerIcon.current.innerText = formatTime(value);
                        }
                        if (timerFullValue.current) {
                            timerFullValue.current.innerText = formatTime(value, true);
                        }
                    }, 1000);
    
                    setCodeTimerInterval(timer);
                }
            }
        }

        const handleCodePanelResize = (event: MouseEvent) => {
            if (handleRef.current) {
                const parentRect =
                    handleRef.current.parentElement?.getBoundingClientRect();
                if (!parentRect) return;
                const handleX = event.clientX - parentRect.left;
                const newFlexValue = handleX / parentRect.width;
                setFlexValue(newFlexValue);
            }
        };

        const handleMouseUp = () => {
            document.removeEventListener("mousemove", handleCodePanelResize);
            document.removeEventListener("mouseup", handleMouseUp);
        };

        if (handleRef.current) {
            handleRef.current.addEventListener("mousedown", () => {
                document.addEventListener("mousemove", handleCodePanelResize);
                document.addEventListener("mouseup", handleMouseUp);
            });
        }


        // if (!timedContest && openForever) {
        //     console.log("Contest is untimed and is open forever. No need to set timer.");
        // }
        // else if (timedContest && !openForever) {
        //     // contest has an endtime as well as duration.
        //     // so we need to figure out which one is shorter,
        //     // say user has lot of time until endTime, we can go with contestDuration,
        //     // but say we only have 2 hours until contest ends, but contestDuration allows 3 hours, we need to allow 
        //     // only 2 hours, and disregard contestDuration.

        //     console.log("Contest is timed and also has an end time. Calculating which is shorter and settimg timer.");

        //     let warnedLessThan10 = false;
        //     let warnedLessThan5 = false;
        //     let warnedLessThan1 = false;

        //     console.log("Setting timer interval.");
        //     console.log(`Contest ends at: ${new Date(endTime)}`)

        //     let diff = new Date(endTime).getTime() - new Date().getTime();
        //     let secondsUntilContestEnds = Math.round(diff / 1000);

        //     let value = Math.min( secondsUntilContestEnds, contestDuration );
        //     console.log(value);

        //     if (codeTimerInterval) {
        //         clearInterval(codeTimerInterval as any);
        //     }

        //     const timer = setInterval(() => {
        //         value--;
        //         if (value <= 0) {
        //             toast.info("Contest has ended.")
        //             clearInterval(timer);
        //             // save all the code, submit a run request, call logout function.
        //             openMessagePopup(
        //                 "Saving your progress...",
        //                 "The contest has ended. Your progress is being automatically saved. Please do not close the tab until this process completes.",
        //                 []
        //             )
        //             setPageToShow("ended");
        //         } 
        //         else if (value <= 60) {
        //             if (!warnedLessThan1) {
        //                 warnedLessThan1 = true;
        //                 toast.warn("Contest ends in less than one minute. Complete fast!");
        //             }
        //         }
        //         else if (value <= 300) {
        //             if (!warnedLessThan5) {
        //                 warnedLessThan5 = true;
        //                 toast.warn("Contest ends in less than 5 minutes.");
        //             }
        //         }
        //         else if (value <= 600) {
        //             if (!warnedLessThan10) {
        //                 warnedLessThan10 = true;
        //                 toast.info("Contest ends in less than 10 minutes.");
        //             }
        //         }
        //         setContestTimerValue(value);

        //         if (timerIcon.current) {
        //             timerIcon.current.innerText = formatTime(value);
        //         }
        //         if (timerFullValue.current) {
        //             timerFullValue.current.innerText = formatTime(value, true);
        //         }
        //     }, 1000);

        //     setCodeTimerInterval(timer);

        // }
        // else if (!timedContest && !openForever) {
        //     // contest is not timed, but still has an end time.
        //     console.log("Contest is untimed and has an end time. Setting timer.");

        //     let warnedLessThan10 = false;
        //     let warnedLessThan5 = false;
        //     let warnedLessThan1 = false;

        //     console.log("Setting timer interval.");
        //     console.log(`Contest ends at: ${new Date(endTime)}`)
        //     // value should be number of seconds left till 'endTime'
        //     let diff = new Date(endTime).getTime() - new Date().getTime();
        //     let value = Math.round(diff / 1000);
        //     console.log(value);

        //     if (codeTimerInterval) {
        //         clearInterval(codeTimerInterval as any);
        //     }

        //     const timer = setInterval(() => {
        //         value--;
        //         if (value <= 0) {
        //             toast.info("Contest has ended.")
        //             clearInterval(timer);
        //             // save all the code, submit a run request, call logout function.
        //             openMessagePopup(
        //                 "Saving your progress...",
        //                 "The contest has ended. Your progress is being automatically saved. Please do not close the tab until this process completes.",
        //                 []
        //             )
        //             setPageToShow("ended");
        //         } 
        //         else if (value <= 60) {
        //             if (!warnedLessThan1) {
        //                 warnedLessThan1 = true;
        //                 toast.warn("Contest ends in less than one minute. Complete fast!");
        //             }
        //         }
        //         else if (value <= 300) {
        //             if (!warnedLessThan5) {
        //                 warnedLessThan5 = true;
        //                 toast.warn("Contest ends in less than 5 minutes.");
        //             }
        //         }
        //         else if (value <= 600) {
        //             if (!warnedLessThan10) {
        //                 warnedLessThan10 = true;
        //                 toast.info("Contest ends in less than 10 minutes.");
        //             }
        //         }
        //         setContestTimerValue(value);

        //         if (timerIcon.current) {
        //             timerIcon.current.innerText = formatTime(value);
        //         }
        //         if (timerFullValue.current) {
        //             timerFullValue.current.innerText = formatTime(value, true);
        //         }
        //     }, 1000);

        //     setCodeTimerInterval(timer);
        // }
        // else if (openForever && timedContest) {
        //     // contest is open forever, but still has a duration.
        //     console.log("Contest is open forever, but has a duration. Setting timer.");
        //     let warnedLessThan10 = false;
        //     let warnedLessThan5 = false;
        //     let warnedLessThan1 = false;

        //     console.log("Setting timer interval.");
        //     let value = contestDuration;
        //     console.log(value);

        //     if (codeTimerInterval) {
        //         clearInterval(codeTimerInterval as any);
        //     }

        //     const timer = setInterval(() => {
        //         value--;
        //         if (value <= 0) {
        //             toast.info("Contest has ended.")
        //             clearInterval(timer);
        //             // save all the code, submit a run request, call logout function.
        //             openMessagePopup(
        //                 "Saving your progress...",
        //                 "The contest has ended. Your progress is being automatically saved. Please do not close the tab until this process completes.",
        //                 []
        //             )
        //             setPageToShow("ended");
        //         } 
        //         else if (value <= 60) {
        //             if (!warnedLessThan1) {
        //                 warnedLessThan1 = true;
        //                 toast.warn("Contest ends in less than one minute. Complete fast!");
        //             }
        //         }
        //         else if (value <= 300) {
        //             if (!warnedLessThan5) {
        //                 warnedLessThan5 = true;
        //                 toast.warn("Contest ends in less than 5 minutes.");
        //             }
        //         }
        //         else if (value <= 600) {
        //             if (!warnedLessThan10) {
        //                 warnedLessThan10 = true;
        //                 toast.info("Contest ends in less than 10 minutes.");
        //             }
        //         }
        //         setContestTimerValue(value);

        //         if (timerIcon.current) {
        //             timerIcon.current.innerText = formatTime(value);
        //         }
        //         if (timerFullValue.current) {
        //             timerFullValue.current.innerText = formatTime(value, true);
        //         }
        //     }, 1000);

        //     setCodeTimerInterval(timer);
        // }

        let tempArray = []; 

        console.log("------------ Problems List ------------");
        console.log(problemsList.value);

        for (let i = 0; i < problemsList.value.length; i++) {
            let problemTestcases: TestcaseType[] = [];
            for (let j = 0; j < problemsList.value[i].testcaseLength; j++) {
                problemTestcases.push({
                    testcaseIndex: j,
                    input: "Hidden",
                    output: "NA",
                    correctOutput: null,
                    score: 0,
                    testCase0Output: "",
                    passedTestcases: 0,
                    run_status: "SUCCESS" as "SUCCESS", // Explicitly type "SUCCESS"
                    run_error: null // Default value
                });
            }

            tempArray.push(problemTestcases);
        }

        setTestcaseResults(tempArray);
        console.log("%c Testcase results initialized.", "color: white; background-color: red");

        // visualize temparray
        /*

        [
            [
                {
                    testcaseIndex: 0,
                    input: "1\n2",
                    output: "NA",
                    correctOutput: false,
                    score: 0
                },
                {
                    testcaseIndex: 1,
                    input: "2\n3",
                    output: "NA",
                    correctOutput: false,
                    score: 0
                },
                {
                    testcaseIndex: 2,
                    input: "3\n4",
                    output: "NA",
                    correctOutput: false,
                    score: 0
                }
            ],
            [
                ...
            ]

        ]
        */

        const newCodeStorage: CodeStorageType = {};

        // Fill blank code for each problem
        problemsList.value.forEach((problem: any) => {
            newCodeStorage[problem.id] = {} as any;
            CodeLanguages.forEach((lang) => {
                newCodeStorage[problem.id][lang] = {
                    original: getCodeForLang(problem, lang),
                    user: ""
                };
            });
        });

        setCodeStorage(newCodeStorage);

        console.log("Set blank values in codeStorage.");

        console.log("---------------------------------->>")
        console.log(contestDuration, endTime, openForever);
        console.log("---------------------------------->>")

        return () => {
            document.removeEventListener("fullscreenchange", handleFullScreenChange);
            window.onblur = null;

            if (timedContest) {
                try {
                    console.log("Clearing timer interval.")
                    clearInterval(codeTimerInterval as ReturnType<typeof setInterval>);
                }
                catch {}    
            }

            if (handleRef.current) {
                handleRef.current.removeEventListener("mousedown", () => {
                    document.removeEventListener("mousemove", handleCodePanelResize);
                    document.removeEventListener("mouseup", handleMouseUp);
                });
            }
        };
    }, []);

    useEffect(() => {

        console.log("%c Code STOARGE VALUE CHANGED BRUHHH!!!", "color: red; background-color: white");
        console.log(codeStorage);

        if (firstTimeCodeStorage && Object.keys(codeStorage).length) {
            console.log("------");
            console.log(firstTimeCodeStorage, codeStorage);
            console.log("First time code storage changing (must be the init script).")
            setFirstTimeCodeStorage(false);
            
            if (type === "contest") {
                changeProblem(0);
            }
            else {
                const urlParts = location.pathname.slice(1).split("/");
                let problemID = urlParts[1];    // assuming the URL is /code/problemID
                let problemIndex = problemsList.value.findIndex((problem: ProblemType) => problem.id === problemID);
                changeProblem(problemIndex);
            }
        }
        else {
            console.log("%c Either first time code storage is false or code storage is null.", "color: orange; background-color: black")
        }

    }, [codeStorage, firstTimeCodeStorage]);


    // SECURITY

    const handleFullScreenChange = () => {
        console.log(`Is full screen: ${document.fullscreenElement}`)
        console.log(document.fullscreenElement);
        
        if (!document.fullscreenElement) {
            let time = 15;
            let fullscreenTimer = setInterval(() => {
                if (time <= 0) {
                    console.log("Eliminated.");
                    clearInterval(fullscreenTimer);
                    clearInterval(codeTimerInterval as ReturnType<typeof setInterval>);
                    setPageToShow("malpractice")
                    // send a malpractice logout request.
                    axios.post("/contests/finish", { contestSlug: contestSlug, reason: "malpractice", cause: "exitfullscreen" }, { headers: { Authorization: `Bearer ${currentUser.accessToken}` } })
                    .then(() => {
                        toast.error("You have been eliminated.")
                    })
                    .catch((e) => {
                        toast.error(e.message);
                    })
                    return;
                }
                setPopupTitle("You will be eliminated.");
                setPopupContent(`Go fullscreen to continue the contest. You have ${time--} seconds to do so.`);
                setPopupButtons(
                    [
                        {
                            label: "Go Fullscreen",
                            onClick: () => {
                                document.documentElement.requestFullscreen();
                                setShowMessagePopup(false);
                                clearInterval(fullscreenTimer);
                            },
                        }
                    ]
                )
                setShowMessagePopup(true);
                console.log(`Come back and go full screen in ${time--}seconds.`)
            }, 1000)
        }
    }

    const handleBlur = () => {
        console.log("Blurred.")
        let time = 15;
        setBlurCount((oldVal) => oldVal + 1);
        let blurTimer = setInterval(() => {
            if (time <= 0) {
                console.log("Eliminated.");
                clearInterval(blurTimer);
                clearInterval(codeTimerInterval as ReturnType<typeof setInterval>);
                setPageToShow("malpractice")
                // send a malpractice logout request.
                axios.post("/contests/finish", { contestSlug: contestSlug, reason: "malpractice", cause: `exitfullscreen (${3} times)` }, { headers: { Authorization: `Bearer ${currentUser.accessToken}` } })
                .then(() => {
                    toast.error("You have been eliminated.")
                })
                .catch((e) => {
                    toast.error(e.message);
                })
                return;
            }
            setPopupTitle("You will be eliminated.");
            setPopupContent(`Come back to the contest window. You have ${time--} seconds to do so.`);
            setPopupButtons(
                [
                    {
                        label: "I'm back",
                        onClick: () => {
                            setShowMessagePopup(false);
                            clearInterval(blurTimer);
                        },
                    }
                ]
            )

            setLoseFocusTimer(blurTimer)
            setShowMessagePopup(true);
            console.log(`Come back in ${time--}seconds.`)
        }, 1000)
    }

    // Helper to check if a value is a valid key of CodeRunStatusKey
    const isValidCodeStatusKey = (key: any): key is CodeRunStatusKey => {
        return [
            "QUEUED",
            "EXECUTING",
            "EXECUTED",
            "QUEUED_FOR_EVALUATION",
            "EVALUATING",
            "DONE",
            "ERROR_NO_SERVERS_ONLINE",
            "ERROR_UNKNOWN"
        ].includes(key);
    };

    return (
        <div className="Code">
            {
                (showMessagePopup) 
                ? 
                    <MessagePopup 
                        close={() => setShowMessagePopup(false)} 
                        title={popupTitle} 
                        content={popupContent}              
                        buttons={popupButtons}
                    /> 
                : 
                    <></>
            }
            {
                (type === "challenge" || type === "contest") && (
                    <nav className="codeNav">
                        <ul>
                            <li
                                className="navItem timer"
                                title="Time remaining"
                                onClick={() => {
                                    console.log(problemsList);
                                }}
                            >
                                <span className="icon" ref={timerIcon}>
                                    <IoChevronBackOutline />
                                </span>
                                <span className="text" ref={timerFullValue}>Best of luck!</span>
                            </li>

                            {problemsList.value.map((problemObj: { title: string | number | boolean | ReactElement<any, string | JSXElementConstructor<any>> | Iterable<ReactNode> | ReactPortal | null | undefined; }, index: number) => {
                                return (
                                    <button
                                        key={index}
                                        className={("navItem" + ((selectedProblemIndex === index) ? " active" : ""))}
                                        onClick={() => {
                                            changeProblem(index);
                                            console.log(index);
                                        }}
                                        disabled={isCodeRunning}
                                    >
                                        <span className="icon">{index + 1}</span>
                                        <span className="text">{problemObj.title}</span>
                                    </button>
                                );
                            })}
                        </ul>
                    </nav>
                )
            }
            {
                (type === "problem") && (
                    <nav className="codeNav">
                        <ul>
                            <li
                                className="navItem timer"
                                onClick={() => {
                                    console.log(problemsList);
                                }}
                            >
                                <span className="icon" ref={timerIcon}>
                                    <IoChevronBackOutline />
                                </span>
                                <CLink to={"/"}>
                                    <span className="text">
                                        Back to Home
                                    </span>
                                </CLink>
                            </li>
                        </ul>
                    </nav>
                )
            }
            <div
                className="codeContent"
                style={{ display: "flex", height: "100%" }}
            >
                <div
                    className="questionContainerBox"
                    style={{ flex: flexValue }}
                >
                    <div className="questionContainer">

                        <div className="box problemStatement">
                            <div 
                                className="title"
                                onClick={() => {
                                    console.log(codeStorage)
                                }}
                            >{ problemsList.value[selectedProblemIndex]?.title }</div>
                            <div className="content">
                                {problemsList.value[selectedProblemIndex]?.description}
                            </div>
                        </div>

                        <div className="box inputOutputFormat">
                            <div className="title"
                                onClick={() => {
                                    console.log(contestSlug)
                                }}
                            >Input/Output Format</div>
                            <div className="content">
                                {problemsList.value[selectedProblemIndex]?.inputFormat?.replaceAll("_b", "\n")}
                                <br />
                                {problemsList.value[selectedProblemIndex]?.outputFormat?.replaceAll("_b", "\n")}
                            </div>
                        </div>

                        <div className="box sampleInput" onClick={() => {
                            console.log(problemsList);
                            console.log(selectedProblemIndex)
                        }}>
                            <div className="title">Sample Input</div>
                            <div className="content">
                                <CodeSnippet codeSnippet={problemsList.value[selectedProblemIndex]?.sampleInput} />
                            </div>
                        </div>

                        <div className="box outputFormat">
                            <div className="title">Sample Output</div>
                            <div className="content">
                                <CodeSnippet codeSnippet={problemsList.value[selectedProblemIndex]?.sampleOutput} />
                            </div>
                        </div>

                        <div className="box splitter">
                        </div>

                        {
                            (type !== "contest") && (
                                <div className="box outputFormat">
                                    <div className="title">Tags</div>
                                    <div className="content tags">
                                        {
                                            (problemsList.value[selectedProblemIndex]?.tags.length) && (
                                                problemsList.value[selectedProblemIndex]?.tags.map((tag: string, index: number) => {
                                                    return (
                                                        <Tag 
                                                            key={index}
                                                            tag={tag}
                                                            openInNewTab
                                                        />
                                                    )
                                                })
                                            )
                                        }
                                    </div>
                                </div>
                            )
                        }
                    </div>
                </div>
                <div className="resizeHandle" ref={handleRef}>
                    ||
                </div>
                <div className="codeEditor" style={{ flex: 1 - flexValue }}>
                    <div className="editorContainer">
                        <div className="editorBars header">
                            
                            {
                                (savingIndicator) ? (
                                    <div className="saveIndicator">
                                        <HiOutlineSave />
                                        {savingIndicator}
                                    </div>
                                ) : (
                                    <></>
                                )
                            }
                            
                            <div className="options">
                                <select
                                    onChange={(e) => {
                                        setPrevChosenLanguage(chosenLanguage);
                                        setChosenLanguage(e.target.value as keyof typeof editorToThemeLangMap);
                                    }}
                                >
                                    <option value="PYTHON3">Python 3</option>
                                    <option value="JAVA8">Java</option>
                                    <option value="CPP17">C++</option>
                                    <option value="JAVASCRIPT_NODE">JavaScript</option>
                                </select>
                                <button className="green" onClick={runCode} disabled={runButtonDisabled}>
                                    Run
                                </button>
                                <button className="orange" onClick={
                                    () => {
                                        // editorRef?.current?.editor.setValue(problemsList.value[selectedProblemIndex].code);
                                        console.log("Resetting code to original.");
                                        console.log()
                                        let tempCodeStorage = codeStorage;
                                        editorRef?.current?.editor.setValue(tempCodeStorage[problemsList.value[selectedProblemIndex].id][chosenLanguage]['original']);
                                        tempCodeStorage[problemsList.value[selectedProblemIndex].id][chosenLanguage]['user'] = tempCodeStorage[problemsList.value[selectedProblemIndex].id][chosenLanguage]['original'];
                                        setCodeStorage(tempCodeStorage);
                                    }
                                }>Reset Code</button>
                                <div className="logout" title="Log out" onClick={() => {
                                    openMessagePopup(
                                        "Are you sure you want to log out?", 
                                        "Your progress will be saved. You may not be able to re-enter as per contest rules.",
                                        [
                                            { label: "Log out", onClick: logOutFromCurrentSession, color: "red" }, 
                                            { label: "Cancel", onClick: "close", color: "white" }
                                        ]
                                    )
                                }} ><IoIosLogOut size={"20px"} /></div>
                            </div>
                        </div>

                        <div className="editor">
                            <AceEditor
                                ref={editorRef}
                                // value={editorCode}
                                mode={editorToThemeLangMap[chosenLanguage] as string}
                                width={"100%"}
                                theme={"tinacious-remix"}
                                // onChange={onChange}
                                name={"aceEditor"}
                                fontSize={14}
                                showPrintMargin={false}
                                showGutter={true}
                                debounceChangePeriod={2000}
                                editorProps={{ $blockScrolling: true }}
                                setOptions={{
                                    enableBasicAutocompletion: (type !== "contest") ? true : false,
                                    enableLiveAutocompletion: (type !== "contest") ? true : false,
                                    enableSnippets: (type !== "contest") ? true : false,
                                    showLineNumbers: true,
                                }}
                                // annotations={[{ row: 0, column: 2, type: 'error', text: 'Some error.'}]}
                                commands={[
                                    {
                                      name: 'customCtrlEnter',
                                      bindKey: { win: 'Ctrl-Enter', mac: 'Command-Enter' },
                                      exec: runCode,
                                    },
                                  ]}
                            />
                        </div>

                        <div className="editorBars bottomBar">
                            <div className="options">
                                <button className="green" onClick={runCode} disabled={runButtonDisabled}>
                                    Run
                                </button>
                                {
                                    (true) ? (
                                        <button className="red" onClick={
                                            () => {
                                                openMessagePopup(
                                                    "Are you sure you want to finish the contest?", 
                                                    "Your progress will be saved.",
                                                    [
                                                        { label: "Finish", onClick: () => finishRound( contestDuration - contestTimerValue ), color: "red" }, 
                                                        { label: "Cancel", onClick: "close", color: "white" }
                                                    ]
                                                )
                                            }
                                        }>Finish</button>
                                    ) : (
                                        <></>
                                    )
                                }
                            </div>
                        </div>

                        <div className="runStatus" ref={runStatusRef}>
                            {
                                (!isCodeRunning) ? (
                                    <AnimatePresence>
                                        <motion.div
                                            initial={{ opacity: 0, scale: .98 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{
                                                duration: .25,
                                                ease: "easeInOut"
                                            }}
                                        >
                                            <div className="testcase0Output">
                                                <div className="panel">
                                                    <div className="title">Testcase 0 Output</div>
                                                    <div className="result">
                                                        {(testcaseResults[selectedProblemIndex]?.length) && (
                                                            // testcase results has something... the code could have been run (output) or could have failed (run_error).
                                                            (testcaseResults[selectedProblemIndex][0]?.run_status === "SUCCESS") ? (
                                                                testcaseResults[selectedProblemIndex][0]?.output
                                                            ) : (testcaseResults[selectedProblemIndex][0]?.run_status === "ERROR") ? (
                                                                // "Error: " + testcaseResults[selectedProblemIndex][0]?.run_error?.code
                                                                "Error: " + testcaseResults[selectedProblemIndex][0]?.run_error
                                                            ) : (
                                                                "NA"
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="panel">
                                                    <div className="title">Expected Output</div>
                                                    <div className="result">
                                                        {sampleOutput}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="testCasesPassed"
                                                onClick={() => {
                                                    console.log(
                                                        testcaseResults
                                                    )
                                                }}
                                            >
                                                Testcases passed: {testcaseResults[selectedProblemIndex]?.filter((testcaseObj) => testcaseObj.correctOutput === true).length} / {testcaseResults[selectedProblemIndex]?.length}
                                            </div>
                                            
                                            <div className="hiddenTestCases"
                                                onClick={() => {
                                                    console.log(testcaseResults)
                                                }}
                                            >
                                                {
                                                    (testcaseResults.length > 0) && (
                                                        Array(testcaseResults[selectedProblemIndex].length).fill(0).map((_, index) => {
                                                            return (
                                                                <div className="testCase" key={index}>
                                                                    <div className="left">Testcase {index}</div>
                                                                    <div 
                                                                        className={`right ${(testcaseResults[selectedProblemIndex][index]?.correctOutput) == true ? "PASSED" : ( (testcaseResults.length > 0) && (testcaseResults[selectedProblemIndex][index]?.correctOutput) == false) ? "FAILED" : "pending"}`}
                                                                    >
                                                                        {
                                                                            (testcaseResults[selectedProblemIndex]?.length) && (
                                                                                (testcaseResults[selectedProblemIndex][index]?.correctOutput === true) ? (
                                                                                    <span className="passed">PASSED</span>
                                                                                ) : (
                                                                                    (testcaseResults[selectedProblemIndex][index]?.correctOutput === false) ? (
                                                                                        <span className="failed">FAILED</span>
                                                                                    ) : (
                                                                                        <span className="pending">NA</span>
                                                                                    )
                                                                                )
                                                                            )
                                                                        }
                                                                    </div>
                                                                </div>      
                                                            )
                                                        })
                                                    )
                                                }
                                            </div>
                                        </motion.div>
                                    </AnimatePresence>
                                ) : (
                                    <motion.div 
                                        className="runningCode"
                                    >
                                        <CodeRunStatus currentStatus={isValidCodeStatusKey(runStatus) ? runStatus : "QUEUED"} />
                                    </motion.div>
                                )
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Code;
